import React, { useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withTranslation } from "react-i18next";
import { withRouter, useParams } from "react-router-dom";
import { Modal, Card, Typography, Spin, Button } from "antd";
import styled from "styled-components";
import { permissionsMap } from "@tailormed/common-client/constant/roles";
import { useLocation } from "react-router";

import { getOrderByIdAPI, SERVER_URL } from "../api/api";
import {
  selectPatients,
  selectPatientCycles,
  selectPatientNotes,
  selectPatientAttachments,
  selectSearchedPatient,
  selectIdPatientModal,
  selectPatientModal,
  selectCurrentCycle,
  selectUser,
  selectFilteredPatientCycles,
  selectFilterList,
  selectSavedListViewType,
  selectViewType
} from "../store/selector";
import { THIS_YEAR } from "../constant/filters";
import { PatientCurrentCycleTab } from "../component/PatientCurrentCycleTab";
import { PatientCyclesTab } from "../component/PatientCyclesTab";
import { PatientCyclesEditor } from "../component/PatientCyclesEditor";
import { PatientNoteTab } from "../component/PatientNoteTab";
import { PatientAttachmentTab } from "../component/PatientAttachmentTab";
import { PatientTaskTab } from "../component/PatientTaskTab";
import { PatientTrackingTab } from "../component/PatientTrackingTab";
import { PatientModalHeader } from "../component/PatientModalHeader";
import CyclesIcon from "../assets/svg/CyclesIcon";
import TrackingIcon from "../assets/svg/TrackingIcon";
import NoteIcon from "../assets/svg/NoteIcon";
import AttachIcon from "../assets/svg/AttachIcon";
import CurrentCycleIcon from "../assets/svg/CurrentCycleIcon";
import CyclesEditorIcon from "../assets/svg/CyclesEditorIcon";
import { ReactComponent as FNLinkIcon } from "../assets/svg/fn-link-icon.svg";
import TaskIcon from "../assets/svg/TaskIcon";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";
import { ContactSupport } from "../component/ContactSupport";
import { SEC_FONT_GREY } from "../constant/colors";
import ACTIONS from "../store/action";
import { Routes } from "../constant/routes";
import { VIEW_TYPES } from "../constant/viewTypes";
import { searchTypes } from "../constant/general";

const ReachableContext = React.createContext();
const { Text } = Typography;

const TabIndexMap = Object.freeze({
  0: "current_cycle",
  1: "cycles",
  2: "cycles_editor",
  3: "notes",
  4: "attachments",
  5: "tasks",
  6: "tracking"
});

const getTabColor = (modalTab, key) => (modalTab === TabIndexMap[key] ? "#2B56A3" : "#9B9B9B");

const FNLinkButtonStyled = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: ${SEC_FONT_GREY};
  cursor: pointer;
  position: relative;
  bottom: 43px;
  left: 950px;
  font-size: 15px;

  :hover,
  :active,
  :focus {
    background: transparent;
    color: ${SEC_FONT_GREY};
  }
`;

const FINANCIAL_NAVIGATION_KEY = "financial-navigation";
const domainName = SERVER_URL;
const NAME_ID = searchTypes.name_id.key;

function PatientViewComponent({
  t,
  patientModal,
  patients,
  patientId,
  patientCycles,
  patientNotes,
  patientAttachments,
  searchedPatient,
  setShowPatientModal,
  clearPatient,
  history
}) {
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();

  const selectedViewType = useSelector(selectViewType);
  const viewType = useParams()?.viewType || selectedViewType;
  const isSearchResultsPage = pathname.includes(Routes.SEARCH_RESULTS);
  const searchParams = new URLSearchParams(search);
  const searchByParams = searchParams.get("searchBy") || NAME_ID;

  const [modal, contextHolder] = Modal.useModal();
  const [showSupport, setShowSupport] = useState(false);
  const [modalTab, setModalTab] = useState(TabIndexMap[patientModal.tab]);
  const [edited, setEdited] = useState(false);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journeyId, setJourneyId] = useState(null);
  const [filteredTasksCount, setFilteredTasksCount] = useState(0);
  const queryParameters = new URLSearchParams(window.location.search);
  const patientIdParams = queryParameters.get("patient");
  const pharmacyPatientIdParams = queryParameters.get("pharmacyPatient");
  const currentCycle = useSelector(selectCurrentCycle);
  const user = useSelector(selectUser);
  const filterList = useSelector(selectFilterList);
  const savedListViewType = useSelector(selectSavedListViewType);
  const filteredPatientCycles = useSelector(selectFilteredPatientCycles);

  const allowedRoles = permissionsMap[FINANCIAL_NAVIGATION_KEY] || [];
  const hasAccessToFN = allowedRoles.includes(user?.role);

  const setViewTypeAct = (viewType) => dispatch(ACTIONS.setViewType(viewType));
  const resetCycleAdminDateFilter = () => {
    dispatch(ACTIONS.setCycleAdminDateFilter({ fromDate: THIS_YEAR, toDate: null }));
  };

  const getOrderById = async (orderId) => {
    const { data } = await getOrderByIdAPI(orderId);
    return data.patients[0];
  };

  useEffect(() => {
    if (patientModal.show) setLoading(true);
  }, [patientModal.show]);

  useEffect(async () => {
    let patient;
    if (searchedPatient) {
      patient = currentCycle;
    } else {
      patient = patients.find((patient) => patient.currentOrder.id === Number(patientId));
    }
    if (!patient && patientId) {
      patient = await getOrderById(patientId);
    }
    setPatient(patient);
  }, [patients, patientId, searchedPatient, currentCycle]);

  useEffect(() => {
    setModalTab(TabIndexMap[patientModal.tab]);
    setShowSupport(false);
  }, [patientModal.tab]);

  useEffect(() => {
    if (currentCycle !== null && currentCycle !== undefined) {
      setLoading(false);
    }
  }, [currentCycle]);

  useEffect(() => {
    const journeyId = currentCycle?.potentialSaving?.journey;
    if (journeyId) {
      setJourneyId(journeyId);
    }
  }, [currentCycle?.potentialSaving?.journey]);

  const handleCancel = () => {
    if (edited) {
      modal.confirm({
        className: "formPopupsModal",
        icon: <WarningIcon />,
        okText: t("yes_sure"),
        cancelText: t("cancel"),
        centered: true,
        title: t("patient.edit_confirm_title"),
        content: t("patient.edit_confirm_content"),
        onOk: () => {
          setEdited(false);
          setShowPatientModal(false);
          setViewTypeAct(viewType);
          clearPatient();
          setShowSupport(false);
          resetCycleAdminDateFilter();
          setModalTab(TabIndexMap[patientModal.tab]);
          if (patientIdParams && pharmacyPatientIdParams) {
            if (filterList?.id) {
              history.replace(
                `${Routes.ROOT}${Routes.SAVEDLISTS}/${filterList.id}/${savedListViewType || VIEW_TYPES.List.key}`
              );
              return;
            }
            if (isSearchResultsPage) {
              history.replace(`${Routes.ROOT}${Routes.SEARCH_RESULTS}?searchBy=${searchByParams}`);
              return;
            }

            history.push(Routes.ROOT + (viewType || VIEW_TYPES.List.key));
          }
        }
      });
    } else {
      setShowPatientModal(false);
      setViewTypeAct(viewType);
      clearPatient();
      setShowSupport(false);
      resetCycleAdminDateFilter();
      setModalTab(TabIndexMap[patientModal.tab]);
      //NOTE :If user click on cancel button within the patient info modal, then clean the patient params from the URL (workaround to not open modal again)
      if (patientIdParams && pharmacyPatientIdParams) {
        if (filterList?.id) {
          history.replace(
            `${Routes.ROOT}${Routes.SAVEDLISTS}/${filterList.id}/${savedListViewType || VIEW_TYPES.List.key}`
          );
          return;
        }
        if (isSearchResultsPage) {
          history.replace(`${Routes.ROOT}${Routes.SEARCH_RESULTS}?searchBy=${searchByParams}`);
          return;
        }
        history.push(Routes.ROOT + (viewType || VIEW_TYPES.List.key));
      }
    }
  };

  const tabList = [
    {
      key: TabIndexMap[0],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-current-tab"
          id="current-cycle-tab"
        >
          <CurrentCycleIcon color={getTabColor(modalTab, 0)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 0)
            }}
          >
            {t("patient.tabs.current_cycle")}
          </Text>
        </div>
      )
    },
    {
      key: TabIndexMap[1],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-all-cyle-tab"
          id="all-cycles-tab"
        >
          <CyclesIcon color={getTabColor(modalTab, 1)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 1)
            }}
          >
            {t("patient.tabs.cycles")}
          </Text>
        </div>
      )
    },
    {
      key: TabIndexMap[2],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-cycles-editor-tab"
          id="cycles-editor-tab"
        >
          <CyclesEditorIcon color={getTabColor(modalTab, 2)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 2)
            }}
          >
            {t("patient.tabs.cycles_editor")}
          </Text>
        </div>
      )
    },
    {
      key: TabIndexMap[3],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-notes-tab"
          id="notes-tab"
        >
          <NoteIcon color={getTabColor(modalTab, 3)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 3)
            }}
          >{`${t("patient.tabs.notes")} (${patientNotes.length})`}</Text>
        </div>
      )
    },
    {
      key: TabIndexMap[4],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-attach-tab"
          id="attachments-tab"
        >
          <AttachIcon color={getTabColor(modalTab, 4)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 4)
            }}
          >{`${t("patient.tabs.attachments")} (${patientAttachments.length})`}</Text>
        </div>
      )
    },
    {
      key: TabIndexMap[5],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-tasks-tab"
          id="tasks-tab"
        >
          <TaskIcon color={getTabColor(modalTab, 5)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 5)
            }}
          >{`${t("patient.tabs.tasks")} (${filteredTasksCount})`}</Text>
        </div>
      )
    },
    {
      key: TabIndexMap[6],
      tab: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          data-tm-id="patient-pg-tracking-tab"
          id="tracking-tab"
        >
          <TrackingIcon color={getTabColor(modalTab, 6)} />
          <Text
            style={{
              fontSize: 15,
              paddingLeft: 6,
              color: getTabColor(modalTab, 6)
            }}
          >
            {t("patient.tabs.tracking")}
          </Text>
        </div>
      )
    }
  ];

  const handleClickFNLink = () => {
    const url = `${domainName}patient/${patient?.patient?.id}/journey/${journeyId}/applications/`;
    window.open(url);
  };

  return (
    <ReachableContext.Provider value={null}>
      <Modal
        centered
        open={patientModal.show}
        destroyOnClose={true}
        width={"90%"}
        bodyStyle={{ minHeight: 680, padding: "24px 24px 0px 24px" }}
        onCancel={handleCancel}
        maskClosable={true}
        footer={null}
      >
        {showSupport && <ContactSupport setShowSupport={setShowSupport} patient={patient} />}
        {!showSupport && loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999
            }}
          >
            <Spin size="large" />
          </div>
        )}
        {!showSupport && (
          <Card
            id="patient_card"
            bordered={false}
            title={currentCycle ? <PatientModalHeader setShowSupport={setShowSupport} patient={currentCycle} /> : null}
            tabList={tabList}
            activeTabKey={modalTab}
            onTabChange={(key) => {
              if (edited) {
                modal.confirm({
                  centered: true,
                  className: "formPopupsModal",
                  icon: <WarningIcon />,
                  okText: t("yes_sure"),
                  cancelText: t("cancel"),
                  title: t("patient.edit_confirm_title"),
                  content: t("patient.edit_confirm_content"),
                  onOk: () => {
                    setModalTab(key);
                    setEdited(false);
                  }
                });
              } else {
                setModalTab(key);
              }
            }}
            headStyle={{ padding: "0px 0px 0px 0px" }}
            bodyStyle={{ padding: "0px 0px 0px 0px" }}
          >
            {hasAccessToFN && (
              <FNLinkButtonStyled id="fn_link_btn" type="text" icon={<FNLinkIcon />} onClick={handleClickFNLink}>
                {t("fn_link")}
              </FNLinkButtonStyled>
            )}

            {currentCycle && modalTab === tabList[0].key && (
              <PatientCurrentCycleTab patient={currentCycle} setEdited={setEdited} />
            )}
            {currentCycle && patient && patientCycles && modalTab === tabList[1].key && (
              <PatientCyclesTab
                patient={patient}
                cycles={filteredPatientCycles}
                isActive={patient.isActive}
                setEdited={setEdited}
                edited={edited}
              />
            )}
            {currentCycle && modalTab === tabList[2].key && (
              <PatientCyclesEditor cycles={patientCycles} patient={currentCycle} setEdited={setEdited} />
            )}
            {currentCycle && modalTab === tabList[3].key && (
              <PatientNoteTab patient={currentCycle} isActive={currentCycle.isActive} setEdited={setEdited} />
            )}
            {currentCycle && modalTab === tabList[4].key && <PatientAttachmentTab patient={currentCycle} />}
            {patient && modalTab === tabList[5].key && (
              <PatientTaskTab
                patient={patient}
                isActive={patient.isActive}
                setEdited={setEdited}
                onFilteredCountChange={setFilteredTasksCount}
              />
            )}
            {patient && modalTab !== tabList[5].key && (
              <div style={{ display: "none" }}>
                <PatientTaskTab
                  patient={patient}
                  isActive={patient.isActive}
                  setEdited={() => {}}
                  onFilteredCountChange={setFilteredTasksCount}
                />
              </div>
            )}
            {modalTab === tabList[6].key && <PatientTrackingTab />}
          </Card>
        )}
      </Modal>
      {contextHolder}
    </ReachableContext.Provider>
  );
}

const mapStateToProps = createStructuredSelector({
  patients: selectPatients,
  patientCycles: selectPatientCycles,
  patientNotes: selectPatientNotes,
  patientAttachments: selectPatientAttachments,
  searchedPatient: selectSearchedPatient,
  patientId: selectIdPatientModal,
  patientModal: selectPatientModal
});

const mapDispatchToProps = (dispatch) => ({
  setShowPatientModal: (showPatientModal) => dispatch(ACTIONS.setShowPatientModal(showPatientModal)),
  clearPatient: () => dispatch(ACTIONS.clearPatient())
});

const PatientView = connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(PatientViewComponent)));

export { PatientView };
